import { db } from "../lib/firebase";
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, runTransaction, serverTimestamp, setDoc, updateDoc, where } from "firebase/firestore";
import { notifyAdmins } from "./email";

export interface WalletData {
  balance: number;
  escrowBalance: number;
  totalEarnings: number;
  reservedBalance?: number;
}

export interface WalletTransaction {
  id?: string; // Add id for client-side usage
  type: "credit" | "debit" | "escrow_hold" | "escrow_release" | "payout_request" | "platform_fee";
  amount: number;
  description: string;
  fee?: number;
  date?: string; // optional preformatted date; otherwise serverTimestamp
  status: "completed" | "held" | "failed" | "pending";
  orderId?: string;
  reference?: string; // payment reference
  method?: "paystack" | "flutterwave" | "internal";
  createdAt?: any; // Firestore Timestamp
}

// Prefer same-origin API base, fallback to env or localhost
const API_BASE = (typeof window !== 'undefined' ? window.location.origin : '')
  || (import.meta.env.VITE_API_BASE_URL as string | undefined)
  || 'http://localhost:5555';

// Referral reward config (optional via env)
const REFERRAL_REWARD_RATE = Number(((import.meta as any).env?.VITE_REFERRAL_REWARD_RATE) || 0);
const REFERRAL_REWARD_FLAT = Number(((import.meta as any).env?.VITE_REFERRAL_REWARD_FLAT) || 0);
const REFERRAL_REWARD_MAX = Number(((import.meta as any).env?.VITE_REFERRAL_REWARD_MAX) || 0);

export async function getWallet(uid: string): Promise<WalletData> {
  const walletRef = doc(db, "wallets", uid);
  const snap = await getDoc(walletRef);
  const data = snap.exists() ? (snap.data() as WalletData) : { balance: 0, escrowBalance: 0, totalEarnings: 0, reservedBalance: 0 };
  // Ensure reservedBalance exists
  if (typeof (data as any).reservedBalance !== 'number') {
    (data as any).reservedBalance = 0;
  }
  return data as WalletData;
}

// Determine seller platform fee rate based on plan
export async function getSellerPlatformFeeRate(_sellerId: string): Promise<number> {
  const { getPlatformFeeRate } = await import('./settings');
  return getPlatformFeeRate();
}

export function subscribeToWalletTransactions(uid: string, callback: (transactions: WalletTransaction[]) => void) {
  const txCol = collection(db, "wallets", uid, "transactions");
  const q = query(txCol, orderBy("createdAt", "desc"));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const transactions: WalletTransaction[] = [];
    snapshot.forEach((doc) => {
      transactions.push({ id: doc.id, ...doc.data() } as WalletTransaction);
    });
    callback(transactions);
  });

  return unsubscribe;
}

export async function fundWallet(uid: string, amount: number, method: "paystack" | "flutterwave", reference: string) {
  const walletRef = doc(db, "wallets", uid);
  const txDocRef = doc(db, 'wallets', uid, 'transactions', `credit_${reference}`);
  let didCredit = false;
  await runTransaction(db, async (tx) => {
    // Idempotency: if this credit reference already exists, skip
    const existingTx = await tx.get(txDocRef);
    if (existingTx.exists()) {
      return; // duplicate call, do nothing
    }

    const snap = await tx.get(walletRef);
    const current = snap.exists() ? (snap.data() as WalletData) : { balance: 0, escrowBalance: 0, totalEarnings: 0 };
    const updated: WalletData = { ...current, balance: (current.balance || 0) + amount };
    tx.set(walletRef, updated, { merge: true });

    // Create the transaction record atomically
    tx.set(txDocRef, {
      type: "credit",
      amount,
      description: `Wallet funding via ${method}`,
      status: "completed",
      reference,
      method,
      createdAt: serverTimestamp(),
    } as any);
    didCredit = true;
  });

  // Notify admins of the wallet funding (skip when transaction was an idempotent no-op)
  if (didCredit) {
    notifyAdmins({
      event: 'wallet_funded',
      subject: `Wallet funded: ₦${amount.toLocaleString()} (${method})`,
      text: `A user just funded their wallet via ${method}.`,
      meta: { uid, amount, method, reference },
    });
  }
}

export async function debitWallet(uid: string, amount: number, description: string, meta?: { reference?: string; orderId?: string }) {
  const walletRef = doc(db, "wallets", uid);
  const refKey = meta?.reference ? `debit_${meta.reference}` : undefined;
  const txDocRef = refKey ? doc(db, 'wallets', uid, 'transactions', refKey) : undefined;

  await runTransaction(db, async (tx) => {
    // If we have a deterministic reference, enforce idempotency
    if (txDocRef) {
      const existing = await tx.get(txDocRef);
      if (existing.exists()) {
        return; // duplicate call, do nothing
      }
    }

    const snap = await tx.get(walletRef);
    const current = snap.exists() ? (snap.data() as WalletData) : { balance: 0, escrowBalance: 0, totalEarnings: 0 };
    if ((current.balance || 0) < amount) throw new Error("Insufficient balance");
    const updated: WalletData = { ...current, balance: (current.balance || 0) - amount };
    tx.set(walletRef, updated, { merge: true });

    const txData: any = {
      type: "debit",
      amount,
      description,
      status: "completed",
      createdAt: serverTimestamp(),
    };
    if (meta?.reference) txData.reference = meta.reference;
    if (meta?.orderId) txData.orderId = meta.orderId;

    if (txDocRef) {
      tx.set(txDocRef, txData);
    } else {
      const autoDoc = doc(collection(walletRef, "transactions"));
      tx.set(autoDoc, txData);
    }
  });
}

// Wrapper: maintain backward-compat for callers expecting withdraw(uid, amount, { description, reference })
export async function withdraw(uid: string, amount: number, options?: { description?: string; reference?: string; orderId?: string }) {
  const desc = options?.description || 'Wallet debit';
  const meta: { reference?: string; orderId?: string } = {};
  if (options?.reference) meta.reference = options.reference;
  if (options?.orderId) meta.orderId = options.orderId;
  await debitWallet(uid, amount, desc, meta);
}

// Add a simple helper to credit wallet for cashback or promos
export async function creditWallet(uid: string, amount: number, description: string, meta?: { orderId?: string }) {
  const walletRef = doc(db, "wallets", uid);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(walletRef);
    const current = snap.exists() ? (snap.data() as WalletData) : { balance: 0, escrowBalance: 0, totalEarnings: 0 };
    const updated: WalletData = { ...current, balance: current.balance + amount };
    tx.set(walletRef, updated, { merge: true });
  });
  const txData: any = {
    type: "credit",
    amount,
    description,
    status: "completed",
    createdAt: serverTimestamp(),
  };
  if (meta?.orderId) txData.orderId = meta.orderId;
  await addDoc(collection(walletRef, "transactions"), txData);
}

// Hold amount in escrow for a seller upon order creation
export async function holdInEscrow(uid: string, amount: number, orderId: string, reference?: string) {
  const walletRef = doc(db, "wallets", uid);
  const txDocRef = doc(db, 'wallets', uid, 'transactions', `escrow_hold_${orderId}`);
  await runTransaction(db, async (tx) => {
    // Idempotency: if we already created this escrow hold for the order, skip
    const existing = await tx.get(txDocRef);
    if (existing.exists()) {
      return;
    }

    const snap = await tx.get(walletRef);
    const current = snap.exists() ? (snap.data() as WalletData) : { balance: 0, escrowBalance: 0, totalEarnings: 0 };
    const updated: WalletData = { ...current, escrowBalance: (current.escrowBalance || 0) + amount };
    tx.set(walletRef, updated, { merge: true });

    const payload: any = {
      type: "escrow_hold",
      amount,
      description: `Escrow hold for order ${orderId}`,
      status: "held",
      orderId,
      createdAt: serverTimestamp(),
    };
    if (reference) payload.reference = reference;
    tx.set(txDocRef, payload as WalletTransaction & { createdAt: any });
  });
}

// Fetch Nigerian bank list from Paystack; fallback to static list
export async function getBanks(): Promise<{ code: string; name: string }[]> {
  try {
    const resp = await fetch(`${API_BASE}/api/paystack/banks`);
    const json = await resp.json().catch(() => ({} as any));
    const list = (json?.data || []) as any[];
    if (Array.isArray(list) && list.length) {
      return list
        .map((b: any) => ({ code: String(b.code || ''), name: String(b.name || '') }))
        .filter(b => b.code && b.name);
    }
  } catch {}
  return [
    { code: '044', name: 'Access Bank' },
    { code: '058', name: 'GTBank' },
    { code: '011', name: 'First Bank' },
    { code: '057', name: 'Zenith Bank' },
    { code: '033', name: 'UBA' },
    { code: '070', name: 'Fidelity Bank' },
    { code: '032', name: 'Union Bank' },
    { code: '076', name: 'Polaris Bank' },
    { code: '035', name: 'Wema Bank' },
    { code: '214', name: 'FCMB' },
    { code: '221', name: 'Stanbic IBTC' },
    { code: '232', name: 'Sterling Bank' },
    { code: '050', name: 'Ecobank' },
    { code: '068', name: 'Standard Chartered' },
    { code: '082', name: 'Keystone Bank' },
    { code: '215', name: 'Unity Bank' },
    { code: '101', name: 'Providus Bank' },
  ];
}
export async function requestPayout(uid: string, amount: number, bank?: { accountNumber?: string; bankCode?: string; accountName?: string }) {
  const walletRef = doc(db, "wallets", uid);

  // 1) Deduct balance immediately while transfer is initiated
  const reference = `PAYOUT-${Date.now()}-${uid}`;
  const txDocRef = doc(db, 'wallets', uid, 'transactions', `payout_${reference}`);
  const retryable = (e: any) => {
    const msg = String(e?.message || '');
    return /failed-precondition|aborted/i.test(msg);
  };
  const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

  const debitAndRecord = async () => {
    await runTransaction(db, async (tx) => {
      const existingTx = await tx.get(txDocRef);
      if (existingTx.exists()) return;

      const snap = await tx.get(walletRef);
      const current = snap.exists() ? (snap.data() as WalletData) : { balance: 0, escrowBalance: 0, totalEarnings: 0 };
      if ((current.balance || 0) < amount) {
        throw new Error("Insufficient balance");
      }
      tx.set(walletRef, { ...current, balance: (current.balance || 0) - amount }, { merge: true });

      const bankData: any = {};
      if (bank?.accountNumber) bankData.accountNumber = bank.accountNumber;
      if (bank?.bankCode) bankData.bankCode = bank.bankCode;
      if (bank?.accountName) bankData.accountName = bank.accountName;
      const txPayload: any = {
        type: "payout_request",
        amount,
        description: "Withdrawal to bank account",
        status: "pending",
        method: "paystack",
        reference,
        createdAt: serverTimestamp(),
      };
      if (Object.keys(bankData).length > 0) txPayload.bank = bankData;
      tx.set(txDocRef, txPayload);
    });
  };

  let lastErr: any = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      await debitAndRecord();
      lastErr = null;
      break;
    } catch (e: any) {
      lastErr = e;
      if (!retryable(e) || attempt === 2) break;
      await sleep(150 * (attempt + 1));
    }
  }
  if (lastErr) throw lastErr;

  // 2) Trigger live Paystack transfer
  const txRef = doc(db, 'wallets', uid, 'transactions', `payout_${reference}`);
  try {
    const resp = await fetch(`${API_BASE}/api/paystack/transfer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        bankCode: bank?.bankCode,
        accountNumber: bank?.accountNumber,
        accountName: bank?.accountName,
        amount,
        reference,
      }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const msg =
        String((data as any)?.message || (data as any)?.error || '').trim()
        || `Transfer failed (${resp.status})`;
      const details = (() => {
        try {
          const s = JSON.stringify(data);
          return s && s !== '{}' ? s : '';
        } catch {
          return '';
        }
      })();
      throw new Error(details ? `${msg}: ${details}` : msg);
    }
    const transferCode = (data as any)?.data?.transfer_code || `transfer-${Date.now()}`;
    const transferStatus = (data as any)?.data?.status;
    // If Paystack immediately confirms (status: 'success'), mark completed now.
    // Otherwise leave as 'pending' — webhook will update when Paystack settles.
    const resolvedStatus = transferStatus === 'success' ? 'completed' : 'pending';
    await updateDoc(txRef, { transferCode, status: resolvedStatus, submittedAt: serverTimestamp() });
  } catch (err: any) {
    // Refund on failure
    const refund = async () => {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(walletRef);
        const current = snap.exists() ? (snap.data() as WalletData) : { balance: 0, escrowBalance: 0, totalEarnings: 0 };
        tx.set(walletRef, { ...current, balance: (current.balance || 0) + amount }, { merge: true });
      });
    };
    let refundErr: any = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await refund();
        refundErr = null;
        break;
      } catch (e: any) {
        refundErr = e;
        if (!retryable(e) || attempt === 2) break;
        await sleep(150 * (attempt + 1));
      }
    }
    if (refundErr) throw refundErr;
    await updateDoc(txRef, { status: 'failed', error: String(err?.message || 'Transfer failed'), updatedAt: serverTimestamp() });
    throw err;
  }
}

export async function releaseEscrowToSeller(
  sellerId: string,
  amount: number,
  orderId: string,
  platformFeeRate?: number
) {
  // Pre-checks to ensure buyer-only release and confirmed delivery
  const orderRef = doc(db, 'orders', orderId);
  const snap = await getDoc(orderRef);
  if (!snap.exists()) throw new Error('Order not found');
  const order = snap.data() as any;

  const delivery = order.delivery || {};
  const buyerConfirmed = Boolean(delivery.buyerConfirmed ?? order.buyerConfirmed);
  const sellerConfirmed = Boolean(delivery.sellerConfirmed ?? order.sellerConfirmed);

  if (!buyerConfirmed || !sellerConfirmed) {
    throw new Error('Delivery not confirmed by both buyer and seller');
  }

  const walletRef = doc(db, 'wallets', sellerId);
  const txCol = collection(db, 'wallets', sellerId, 'transactions');

  // Removed duplicate pre-check block to avoid redeclarations
  const escrowRef = `escrow_${orderId}`;
  const existingQ = query(txCol, where('type', '==', 'escrow_release'), where('reference', '==', escrowRef));
  const existingSnap = await getDocs(existingQ);
  if (!existingSnap.empty) {
    return; // already processed
  }

  await runTransaction(db, async (t) => {
    const wSnap = await t.get(walletRef);
    const wData = wSnap.exists() ? (wSnap.data() as any) : { balance: 0, escrowBalance: 0, totalEarnings: 0 };
    const platformFee = Math.round(amount * (platformFeeRate ?? 0) * 100) / 100;
    const netAmount = amount - platformFee;

    const nextEscrow = Math.max(0, (wData.escrowBalance || 0) - amount);
    const nextBalance = (wData.balance || 0) + netAmount;
    const nextEarnings = (wData.totalEarnings || 0) + netAmount;

    t.set(walletRef, { ...wData, escrowBalance: nextEscrow, balance: nextBalance, totalEarnings: nextEarnings }, { merge: true });

    const txRef = escrowRef;
    const txData = {
      type: 'escrow_release',
      amount: netAmount,
      fee: platformFee,
      description: `Escrow release for order ${orderId}`,
      status: 'completed',
      reference: txRef,
      method: 'internal',
      createdAt: serverTimestamp(),
    };

    // Only record platform fee when greater than zero
    if (platformFee > 0) {
      const feeTxData = {
        type: 'platform_fee',
        amount: platformFee,
        description: `Platform fee on order ${orderId}`,
        status: 'completed',
        reference: `${txRef}_fee`,
        method: 'internal',
        createdAt: serverTimestamp(),
      };

      const feeTxRefDoc = doc(txCol);
      t.set(feeTxRefDoc, feeTxData);
    }

    const txRefDoc = doc(txCol);
    t.set(txRefDoc, txData);

    // Update the order status to completed and add completedAt timestamp
    const orderDocRef = doc(db, 'orders', orderId);
    t.update(orderDocRef, {
      status: 'completed',
      completedAt: serverTimestamp(),
    });
  });

  // After releasing to seller, decrement buyer reserved balance using order context
  try {
    const orderSnap = await getDoc(doc(db, 'orders', orderId));
    if (orderSnap.exists()) {
      const orderData = orderSnap.data() as any;
      const buyerId = orderData?.buyerId;
      if (buyerId) {
        await decreaseBuyerReserved(buyerId, amount);
      }

      // Credit referral bonus to the buyer's referrer (idempotent)
      const rewardAmountCalc = () => {
        const base = REFERRAL_REWARD_FLAT > 0
          ? REFERRAL_REWARD_FLAT
          : Math.round(amount * REFERRAL_REWARD_RATE * 100) / 100;
        if (REFERRAL_REWARD_MAX > 0) return Math.min(base, REFERRAL_REWARD_MAX);
        return base;
      };
      const rewardAmount = rewardAmountCalc();
      if (buyerId && rewardAmount > 0) {
        const buyerSnap = await getDoc(doc(db, 'users', buyerId));
        const buyerData = buyerSnap.exists() ? (buyerSnap.data() as any) : null;
        const referrerId = buyerData?.referredBy;
        if (typeof referrerId === 'string' && referrerId) {
          const refTxCol = collection(db, 'wallets', referrerId, 'transactions');
          const rewardRef = `referral_reward_${orderId}`;
          const rewardQ = query(refTxCol, where('reference', '==', rewardRef));
          const rewardSnap = await getDocs(rewardQ);
          if (rewardSnap.empty) {
            await runTransaction(db, async (t) => {
              const refWalletRef = doc(db, 'wallets', referrerId);
              const refWalletSnap = await t.get(refWalletRef);
              const refWalletData = refWalletSnap.exists() ? (refWalletSnap.data() as any) : { balance: 0, escrowBalance: 0, totalEarnings: 0, reservedBalance: 0 };
              const nextBalance = (refWalletData.balance || 0) + rewardAmount;
              t.set(refWalletRef, { ...refWalletData, balance: nextBalance }, { merge: true });

              const txDoc = doc(refTxCol);
              t.set(txDoc, {
                type: 'credit',
                amount: rewardAmount,
                description: `Referral bonus for order ${orderId}`,
                status: 'completed',
                reference: rewardRef,
                method: 'internal',
                createdAt: serverTimestamp(),
              });
            });
          }
        }
      }
    }
  } catch {}
}

// --- Withdrawal PIN helpers ---
async function sha256(text: string): Promise<string> {
  try {
    const enc = new TextEncoder().encode(text);
    const buf = await crypto.subtle.digest('SHA-256', enc);
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Fallback (NOT secure, but avoids total failure in unsupported environments)
    return String(text);
  }
}

export async function getWithdrawalPinHash(uid: string): Promise<string | null> {
  const snap = await getDoc(doc(db, 'users', uid));
  const data = snap.data() as any;
  const hash = data?.security?.withdrawalPinHash || null;
  return hash || null;
}

export async function setWithdrawalPin(uid: string, pin: string): Promise<void> {
  if (!/^\d{6}$/.test(pin)) throw new Error('PIN must be 6 digits');
  const hash = await sha256(pin);
  await setDoc(doc(db, 'users', uid), { security: { withdrawalPinHash: hash } }, { merge: true });
}

export async function verifyWithdrawalPin(uid: string, pin: string): Promise<boolean> {
  const existing = await getWithdrawalPinHash(uid);
  if (!existing) return false;
  const input = await sha256(pin);
  return existing === input;
}

// Helper: increase buyer reserved balance after debit
export async function increaseBuyerReserved(uid: string, amount: number) {
  const walletRef = doc(db, 'wallets', uid);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(walletRef);
    const current = snap.exists() ? (snap.data() as WalletData) : { balance: 0, escrowBalance: 0, totalEarnings: 0, reservedBalance: 0 };
    const nextReserved = (current.reservedBalance || 0) + amount;
    tx.set(walletRef, { ...current, reservedBalance: nextReserved }, { merge: true });
  });
}

// Helper: decrease buyer reserved balance when order completes/cancels
export async function decreaseBuyerReserved(uid: string, amount: number) {
  const walletRef = doc(db, 'wallets', uid);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(walletRef);
    const current = snap.exists() ? (snap.data() as WalletData) : { balance: 0, escrowBalance: 0, totalEarnings: 0, reservedBalance: 0 };
    const nextReserved = Math.max(0, (current.reservedBalance || 0) - amount);
    tx.set(walletRef, { ...current, reservedBalance: nextReserved }, { merge: true });
  });
}

// Helper to update escrow hold transaction status
export async function updateEscrowHoldStatus(sellerId: string, orderId: string, status: WalletTransaction['status']) {
  const txCol = collection(db, 'wallets', sellerId, 'transactions');
  const qRef = query(txCol, where('type', '==', 'escrow_hold'), where('orderId', '==', orderId));
  const snap = await getDocs(qRef);
  if (snap.empty) return;
  const target = snap.docs[0];
  await updateDoc(target.ref, { status, updatedAt: serverTimestamp() } as any);
}
